import React from 'react';
import I18n from 'i18n-js';
import { RCIcon, RCSegmentedControl } from '@reverbdotcom/cadence/components';
import { ThemeType } from '@reverbdotcom/cadence/components/types';
import { getItem, setItem } from '../local_storage_utils';
import { SunIcon, MoonIcon } from '@reverbdotcom/cadence/icons/react';

/**
 * README
 *
 * This component is intended to be used for debug purposes.
 * Please reach out to #cadence if you have any questions or concerns.
 */
export function WebThemeControl() {
  const [theme, setTheme] = React.useState<ThemeType>(ThemeType.default);
  const LIGHT = ThemeType.light;
  const DARK = ThemeType.dark;

  React.useLayoutEffect(() => {
    const localStorageKey = getItem('reverb-theme') as ThemeType;
    const isValidKey = Object.values(ThemeType).includes(localStorageKey);

    // Override legacy web / web2 themes
    if (!isValidKey) {
      return updateTheme(ThemeType.default);
    }

    return updateTheme(localStorageKey);
  }, []);

  const updateTheme = React.useCallback((newTheme: ThemeType) => {
    setTheme(newTheme);
    setElementAttribute(newTheme);
    setItem('reverb-theme', newTheme);
  }, [setTheme]);

  function setElementAttribute(themeName) {
    const htmlTag = document.getElementsByTagName('html')?.item(0);
    if (!htmlTag) return;

    htmlTag.setAttribute('data-theme', themeName);
  }

  function handleClick(newTheme) {
    if (newTheme === theme) return;
    updateTheme(newTheme);
  }

  return (
    <div className="web-theme-control">
      <RCSegmentedControl
        aria-label={I18n.t('commons.webThemeControl.theme')}
        size="small"
      >
        <RCSegmentedControl.Button
          selected={theme === LIGHT}
          onClick={() => handleClick(LIGHT)}
          aria-label={I18n.t('commons.webThemeControl.light')}
          iconOnly
        >
          <RCIcon svgComponent={() => <SunIcon />} />
        </RCSegmentedControl.Button>
        <RCSegmentedControl.Button
          selected={theme === DARK}
          onClick={() => handleClick(DARK)}
          aria-label={I18n.t('commons.webThemeControl.dark')}
          iconOnly
        >
          <RCIcon svgComponent={() => <MoonIcon />} />
        </RCSegmentedControl.Button>
      </RCSegmentedControl>
    </div>
  );
}
